import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { overtimeAPI } from "../../lib/api";
import { to12h, formatHours, LIEU_STATUS_STYLES } from "../../lib/overtime";
import { OvertimeEntryCard } from "./OvertimeCalendar";
import { ArrowLeft, Pencil, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";

export function TeamOverview() {
  const [summary, setSummary] = useState([]);
  const [lieuRequests, setLieuRequests] = useState([]);
  const [detail, setDetail] = useState(null);
  const [editEntry, setEditEntry] = useState(null);
  const [editForm, setEditForm] = useState({});

  const loadData = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([overtimeAPI.adminSummary(), overtimeAPI.adminLieuRequests()]);
      setSummary(s.data);
      setLieuRequests(l.data);
    } catch (err) {
      toast.error("Failed to load team overtime data");
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openDetail = async (userId) => {
    try {
      const res = await overtimeAPI.adminUserDetail(userId);
      setDetail(res.data);
    } catch (err) {
      toast.error("Failed to load staff breakdown");
    }
  };

  const decideLieu = async (req, action) => {
    try {
      await overtimeAPI.adminDecideLieu(req.id, { action });
      toast.success(`Request ${action === "approve" ? "approved" : "denied"}`);
      loadData();
      if (detail) openDetail(detail.user.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update request");
    }
  };

  const startEdit = (entry) => {
    setEditEntry(entry);
    setEditForm({ date: entry.date, start_time: entry.start_time, end_time: entry.end_time, note: entry.note || "" });
  };

  const saveEdit = async () => {
    try {
      await overtimeAPI.adminUpdateEntry(editEntry.id, editForm);
      toast.success("Entry updated (change recorded in history)");
      setEditEntry(null);
      loadData();
      if (detail) openDetail(detail.user.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update entry");
    }
  };

  const deleteEntry = async (entry) => {
    if (!window.confirm(`Delete this overtime entry (${entry.date}, ${formatHours(entry.hours)})?`)) return;
    try {
      await overtimeAPI.adminDeleteEntry(entry.id);
      toast.success("Entry deleted");
      loadData();
      if (detail) openDetail(detail.user.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete entry");
    }
  };

  const pendingRequests = lieuRequests.filter((r) => r.status === "pending");

  if (detail) {
    return (
      <div className="space-y-4" data-testid="team-staff-detail">
        <Button variant="outline" size="sm" onClick={() => setDetail(null)} data-testid="team-back-button">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to team
        </Button>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{detail.user.full_name || detail.user.username} — Overtime Breakdown</CardTitle>
            <CardDescription>
              Total: <b>{formatHours(detail.total_overtime)}</b> • Time in lieu taken: <b>{formatHours(detail.approved_lieu)}</b> • Balance: <b>{formatHours(detail.balance)}</b>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {detail.entries.length === 0 ? (
              <p className="text-sm text-slate-500">No overtime entries.</p>
            ) : (
              detail.entries.map((entry) => (
                <div key={entry.id} className="flex items-start gap-2">
                  <div className="flex-1">
                    <p className="text-xs font-medium text-slate-500 mb-1">
                      {new Date(entry.date + "T00:00:00").toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
                    </p>
                    <OvertimeEntryCard entry={entry} />
                  </div>
                  <div className="flex flex-col gap-1 pt-5">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => startEdit(entry)} data-testid={`team-edit-entry-${entry.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => deleteEntry(entry)} data-testid={`team-delete-entry-${entry.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Time in Lieu Requests</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {detail.lieu_requests.length === 0 ? (
              <p className="text-sm text-slate-500">No requests.</p>
            ) : (
              detail.lieu_requests.map((r) => (
                <LieuRow key={r.id} req={r} onDecide={decideLieu} showUser={false} />
              ))
            )}
          </CardContent>
        </Card>

        <EditDialog editEntry={editEntry} editForm={editForm} setEditForm={setEditForm} onSave={saveEdit} onClose={() => setEditEntry(null)} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-overview">
      {pendingRequests.length > 0 && (
        <Card className="border-amber-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-amber-700">Pending Time in Lieu Requests ({pendingRequests.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2" data-testid="team-pending-lieu">
            {pendingRequests.map((r) => (
              <LieuRow key={r.id} req={r} onDecide={decideLieu} showUser />
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Staff Overtime Totals</CardTitle>
          <CardDescription>Click a staff member to see their individual breakdown.</CardDescription>
        </CardHeader>
        <CardContent>
          <Table data-testid="team-summary-table">
            <TableHeader>
              <TableRow>
                <TableHead>Staff</TableHead>
                <TableHead className="text-right">Total Overtime</TableHead>
                <TableHead className="text-right">Lieu Taken</TableHead>
                <TableHead className="text-right">Balance</TableHead>
                <TableHead className="text-right">Pending</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {summary.map((s) => (
                <TableRow key={s.user_id} className="cursor-pointer hover:bg-slate-50" onClick={() => openDetail(s.user_id)} data-testid={`team-row-${s.username}`}>
                  <TableCell className="font-medium">
                    {s.full_name || s.username}
                    <span className="text-xs text-slate-400 ml-2 capitalize">{s.role}</span>
                  </TableCell>
                  <TableCell className="text-right font-semibold">{formatHours(s.total_overtime)}</TableCell>
                  <TableCell className="text-right">{formatHours(s.approved_lieu)}</TableCell>
                  <TableCell className="text-right text-blue-700 font-semibold">{formatHours(s.balance)}</TableCell>
                  <TableCell className="text-right">
                    {s.pending_requests > 0 ? <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">{s.pending_requests}</Badge> : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">All Time in Lieu Requests</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2" data-testid="team-all-lieu">
          {lieuRequests.length === 0 ? (
            <p className="text-sm text-slate-500">No requests yet.</p>
          ) : (
            lieuRequests.map((r) => <LieuRow key={r.id} req={r} onDecide={decideLieu} showUser />)
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function LieuRow({ req, onDecide, showUser }) {
  return (
    <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 flex items-center justify-between gap-3" data-testid={`team-lieu-${req.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {showUser && <span className="font-medium text-slate-800">{req.full_name || req.username}</span>}
          <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">{formatHours(req.hours)}</Badge>
          <Badge className={`${LIEU_STATUS_STYLES[req.status]} hover:bg-opacity-100 capitalize`}>{req.status}</Badge>
        </div>
        <p className="text-xs text-slate-600 mt-1 truncate">{req.reason}</p>
        <p className="text-xs text-slate-400">
          {new Date(req.requested_date).toLocaleDateString("en-AU")}
          {req.decided_by && ` • ${req.status} by ${req.decided_by}`}
        </p>
      </div>
      {req.status === "pending" && (
        <div className="flex gap-2 shrink-0">
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8" onClick={() => onDecide(req, "approve")} data-testid={`team-approve-${req.id}`}>
            <Check className="w-4 h-4 mr-1" /> Approve
          </Button>
          <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 h-8" onClick={() => onDecide(req, "deny")} data-testid={`team-deny-${req.id}`}>
            <X className="w-4 h-4 mr-1" /> Deny
          </Button>
        </div>
      )}
    </div>
  );
}

function EditDialog({ editEntry, editForm, setEditForm, onSave, onClose }) {
  return (
    <Dialog open={!!editEntry} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="team-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit Overtime Entry</DialogTitle>
          <DialogDescription>Changes are recorded in the entry's history, visible to the staff member.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-sm">Date</Label>
            <Input type="date" className="mt-1" value={editForm.date || ""} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} data-testid="team-edit-date" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Start Time</Label>
              <Input type="time" className="mt-1" value={editForm.start_time || ""} onChange={(e) => setEditForm({ ...editForm, start_time: e.target.value })} data-testid="team-edit-start" />
            </div>
            <div>
              <Label className="text-sm">End Time</Label>
              <Input type="time" className="mt-1" value={editForm.end_time || ""} onChange={(e) => setEditForm({ ...editForm, end_time: e.target.value })} data-testid="team-edit-end" />
            </div>
          </div>
          <div>
            <Label className="text-sm">Note</Label>
            <Input className="mt-1" value={editForm.note || ""} onChange={(e) => setEditForm({ ...editForm, note: e.target.value })} data-testid="team-edit-note" />
          </div>
          <p className="text-xs text-amber-600">Changes are recorded in the entry's edit history, visible to the staff member.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={onSave} className="bg-blue-600 hover:bg-blue-700" data-testid="team-edit-save">Save Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
